export type AnalyticsAuthResult =
  | 'sent'
  | 'cancelled'
  | 'missing-token'
  | 'blocked'
  | 'timeout'
  | 'failed';

export type AnalyticsTokenSource = {
  readonly getAccessToken: () => string | null;
  readonly getFreshAccessToken: () => Promise<string | null>;
};

const createRequestId = (): string => {
  // getRandomValues also works on the existing HTTP intranet deployments.
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
};

export const openAnalytics = (
  path: string,
  tokens: AnalyticsTokenSource,
  signal: AbortSignal,
): Promise<AnalyticsAuthResult> =>
  new Promise((resolve) => {
    if (signal.aborted) return resolve('cancelled');
    if (!tokens.getAccessToken()) return resolve('missing-token');

    const url = new URL(path);
    const requestId = createRequestId();
    url.searchParams.set('sourceOrigin', window.location.origin);
    url.searchParams.set('requestId', requestId);

    let analyticsWindow: Window | null = null;
    let timeout: number | undefined;
    let closeCheck: number | undefined;
    let settled = false;
    let preparingToken = false;

    const finish = (result: AnalyticsAuthResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', handleMessage);
      signal.removeEventListener('abort', cancel);
      window.clearTimeout(timeout);
      window.clearInterval(closeCheck);
      resolve(result);
    };
    const cancel = () => finish('cancelled');
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (
        !analyticsWindow ||
        preparingToken ||
        settled ||
        analyticsWindow.closed ||
        event.origin !== url.origin ||
        event.source !== analyticsWindow
      ) {
        return;
      }
      const data = event.data;
      if (
        typeof data !== 'object' ||
        data === null ||
        !('type' in data) ||
        data.type !== 'ANALYTICS_AUTH_READY' ||
        !('requestId' in data) ||
        data.requestId !== requestId
      ) {
        return;
      }
      preparingToken = true;
      window.clearTimeout(timeout);
      // Analytics removes its token listener 15 seconds after READY.
      timeout = window.setTimeout(() => finish('timeout'), 14_000);
      void tokens
        .getFreshAccessToken()
        .then((accessToken) => {
          if (settled) return;
          // Recheck after refresh: logout/account changes must deny transfer.
          if (!accessToken || tokens.getAccessToken() !== accessToken) {
            finish('missing-token');
            return;
          }
          if (analyticsWindow?.closed) return cancel();
          analyticsWindow?.postMessage(
            { type: 'ANALYTICS_AUTH_TOKEN', requestId, accessToken },
            url.origin,
          );
          finish('sent');
        })
        .catch(() => finish('failed'));
    };

    window.addEventListener('message', handleMessage);
    signal.addEventListener('abort', cancel, { once: true });
    try {
      // SSO requires window.opener. Never pass noopener/noreferrer here.
      analyticsWindow = window.open(url.href, '_blank');
    } catch {
      finish('blocked');
      return;
    }
    if (!analyticsWindow) return finish('blocked');
    timeout = window.setTimeout(() => finish('timeout'), 30_000);
    closeCheck = window.setInterval(() => {
      if (analyticsWindow?.closed) cancel();
    }, 500);
  });
